import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImageUploadField } from "@/components/admin/image-upload-field";
import { toast } from "sonner";
import { Palette, Type, LayoutTemplate, Moon, Code2, Image as ImageIcon } from "lucide-react";

import { FONT_PAIRS, type Appearance } from "@/lib/appearance";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";

export const Route = createFileRoute("/_authenticated/admin/appearance")({
  component: AppearanceAdmin,
});

const DEFAULT: Appearance = {
  default_theme: "dark",
  allow_user_override: true,
  header_preset: "default",
  footer_preset: "default",
  colors: {},
};

function AppearanceAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["site_settings", "appearance"],
    queryFn: async (): Promise<Appearance> => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "appearance")
        .maybeSingle();
      return { ...DEFAULT, ...((data?.value as Appearance) ?? {}) };
    },
  });

  const [state, setState] = useState<Appearance>(DEFAULT);
  useEffect(() => { if (data) setState(data); }, [data]);

  const save = useMutation({
    mutationFn: async (value: Appearance) => {
      const { error } = await supabase
        .from("site_settings")
        .upsert({ key: "appearance", value });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site_settings", "appearance"] });
      qc.invalidateQueries({ queryKey: ["appearance"] });
      toast.success("Appearance saved — refresh public pages to see changes.");
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const merge = (patch: Partial<Appearance>) => setState((s) => ({ ...s, ...patch }));
  const colors = state.colors ?? {};
  const setColor = (k: keyof NonNullable<Appearance["colors"]>, v: string) =>
    merge({ colors: { ...colors, [k]: v || undefined } });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <AdminPageHeader
          title="Theme & appearance"
          description="Customize the public site's logo, colors, fonts, and custom CSS."
        />
        <Button onClick={() => save.mutate(state)} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save all changes"}
        </Button>
      </div>

      <Tabs defaultValue="logos">
        <PillTabsList search className="flex flex-wrap">
          <PillTabsTrigger value="logos"><ImageIcon className="mr-1.5 h-4 w-4" />Logos & favicons</PillTabsTrigger>
          <PillTabsTrigger value="colors"><Palette className="mr-1.5 h-4 w-4" />Colors</PillTabsTrigger>
          <PillTabsTrigger value="typography"><Type className="mr-1.5 h-4 w-4" />Typography</PillTabsTrigger>
          <PillTabsTrigger value="layout"><LayoutTemplate className="mr-1.5 h-4 w-4" />Layout presets</PillTabsTrigger>
          <PillTabsTrigger value="mode"><Moon className="mr-1.5 h-4 w-4" />Default mode</PillTabsTrigger>
          <PillTabsTrigger value="css"><Code2 className="mr-1.5 h-4 w-4" />Custom CSS</PillTabsTrigger>
        </PillTabsList>

        {/* Logos & favicons */}
        <TabsContent value="logos" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Logos & favicons</CardTitle>
              <CardDescription>Upload light and dark variants. Falls back to defaults when empty.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5 md:grid-cols-2">
              <ImageField
                label="Logo (for light backgrounds)"
                value={state.logo_dark_url}
                onChange={(v) => merge({ logo_dark_url: v })}
                preview="light"
              />
              <ImageField
                label="Logo (for dark backgrounds)"
                value={state.logo_light_url}
                onChange={(v) => merge({ logo_light_url: v })}
                preview="dark"
              />
              <ImageField
                label="Icon (square mark)"
                value={state.logo_icon_url}
                onChange={(v) => merge({ logo_icon_url: v })}
              />
              <ImageField
                label="Favicon"
                value={state.favicon_url}
                onChange={(v) => merge({ favicon_url: v })}
              />
              <ImageField
                label="Apple touch icon (180×180)"
                value={state.apple_touch_icon_url}
                onChange={(v) => merge({ apple_touch_icon_url: v })}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Brand colors */}
        <TabsContent value="colors" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Brand color tokens</CardTitle>
              <CardDescription>Any CSS color (hex, oklch, rgb). Leave blank to inherit defaults.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <ColorField label="Primary" value={colors.primary} onChange={(v) => setColor("primary", v)} />
              <ColorField label="Brand 1 (gradient start)" value={colors.brand} onChange={(v) => setColor("brand", v)} />
              <ColorField label="Brand 2 (gradient end)" value={colors.brand_2} onChange={(v) => setColor("brand_2", v)} />
              <ColorField label="Accent" value={colors.accent} onChange={(v) => setColor("accent", v)} />
              <ColorField label="Background (light)" value={colors.background_light} onChange={(v) => setColor("background_light", v)} />
              <ColorField label="Background (dark)" value={colors.background_dark} onChange={(v) => setColor("background_dark", v)} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Typography */}
        <TabsContent value="typography" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Typography</CardTitle>
              <CardDescription>Pick a Google-Fonts pair, or define a custom one.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label>Preset font pair</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={pairKeyFromState(state) ?? ""}
                  onChange={(e) => {
                    const key = e.target.value;
                    if (!key) return merge({ typography: undefined });
                    merge({ typography: FONT_PAIRS[key] });
                  }}
                >
                  <option value="">— Default (Inter / Space Grotesk) —</option>
                  {Object.entries(FONT_PAIRS).map(([k, p]) => (
                    <option key={k} value={k}>{p.heading} + {p.body}</option>
                  ))}
                </select>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="grid gap-1">
                  <Label>Heading font name</Label>
                  <Input
                    value={state.typography?.heading ?? ""}
                    onChange={(e) => merge({ typography: { ...(state.typography ?? { heading: "", body: "" }), heading: e.target.value } })}
                    placeholder="Space Grotesk"
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Body font name</Label>
                  <Input
                    value={state.typography?.body ?? ""}
                    onChange={(e) => merge({ typography: { ...(state.typography ?? { heading: "", body: "" }), body: e.target.value } })}
                    placeholder="Inter"
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Heading Google Fonts query (optional)</Label>
                  <Input
                    value={state.typography?.headingHref ?? ""}
                    onChange={(e) => merge({ typography: { ...(state.typography ?? { heading: "", body: "" }), headingHref: e.target.value } })}
                    placeholder="Space+Grotesk:wght@500;600;700"
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Body Google Fonts query (optional)</Label>
                  <Input
                    value={state.typography?.bodyHref ?? ""}
                    onChange={(e) => merge({ typography: { ...(state.typography ?? { heading: "", body: "" }), bodyHref: e.target.value } })}
                    placeholder="Inter:wght@400;500;600;700"
                  />
                </div>
              </div>
              {state.typography?.heading && (
                <div className="rounded-md border border-border bg-card p-5">
                  <div style={{ fontFamily: `"${state.typography.heading}", sans-serif` }} className="text-3xl font-bold">
                    The quick brown fox jumps over the lazy dog
                  </div>
                  <p style={{ fontFamily: `"${state.typography.body || state.typography.heading}", sans-serif` }} className="mt-2 text-sm text-muted-foreground">
                    Body sample — pack my box with five dozen liquor jugs. 1234567890.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Layout presets */}
        <TabsContent value="layout" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Header / footer layout presets</CardTitle>
              <CardDescription>Stored for future layout rendering hooks.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="grid gap-2">
                <Label>Header preset</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={state.header_preset ?? "default"}
                  onChange={(e) => merge({ header_preset: e.target.value as any })}
                >
                  <option value="default">Default — logo left, nav center, CTA right</option>
                  <option value="centered">Centered — logo center, nav below</option>
                  <option value="minimal">Minimal — logo + single CTA</option>
                  <option value="split">Split — nav both sides of logo</option>
                </select>
              </div>
              <div className="grid gap-2">
                <Label>Footer preset</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={state.footer_preset ?? "default"}
                  onChange={(e) => merge({ footer_preset: e.target.value as any })}
                >
                  <option value="default">Default — columns + newsletter</option>
                  <option value="compact">Compact — single row</option>
                  <option value="mega">Mega — sitemap with descriptions</option>
                </select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Default theme */}
        <TabsContent value="mode" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Default mode</CardTitle>
              <CardDescription>Initial theme for first-time visitors.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 md:max-w-xs">
                <Label>Default theme</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={state.default_theme ?? "dark"}
                  onChange={(e) => merge({ default_theme: e.target.value as any })}
                >
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                  <option value="system">Follow system</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={state.allow_user_override !== false}
                  onCheckedChange={(v) => merge({ allow_user_override: v })}
                />
                <Label>Allow visitors to override and remember their choice</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom CSS */}
        <TabsContent value="css" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Custom CSS</CardTitle>
              <CardDescription>Injected at the end of every public page. Use with care.</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={14}
                value={state.custom_css ?? ""}
                onChange={(e) => merge({ custom_css: e.target.value })}
                placeholder={`/* Example */\n.site-header { backdrop-filter: blur(12px); }\n:root { --radius: 1rem; }`}
                className="font-mono text-xs"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate(state)} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save all changes"}
        </Button>
      </div>
    </div>
  );
}

function pairKeyFromState(s: Appearance): string | null {
  if (!s.typography) return null;
  for (const [k, p] of Object.entries(FONT_PAIRS)) {
    if (p.heading === s.typography.heading && p.body === s.typography.body) return k;
  }
  return null;
}

function ColorField({ label, value, onChange }: { label: string; value?: string; onChange: (v: string) => void }) {
  const isHex = !!value && /^#([0-9a-f]{3,8})$/i.test(value);
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={isHex ? value! : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 rounded-md border border-input bg-background"
        />
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="#4378BC or oklch(...)" />
      </div>
    </div>
  );
}

function ImageField({
  label, value, onChange, preview,
}: {
  label: string;
  value?: string;
  onChange: (v: string | undefined) => void;
  preview?: "light" | "dark";
}) {
  return (
    <ImageUploadField
      label={label}
      value={value}
      onChange={onChange}
      preview={preview ?? "auto"}
      folder="brand"
    />
  );
}
